import { useEffect, useState } from "react";
import { api, BACKEND_URL, formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, Edit, Check, X, Users, Video, FileText, Crown, ListPlus, Copy, Link2, Download, GripVertical, ChevronDown, ChevronRight } from "lucide-react";
import { extractYouTubeId } from "../lib/youtube";
import { invalidatePageContent } from "../lib/pageContent";

const Empty = ({ children }) => (
  <div className="p-10 text-center text-muted2 bg-cardm border border-subtle rounded-2xl">{children}</div>
);

const Stat = ({ label, value, icon }) => (
  <div className="bg-white border border-subtle rounded-2xl p-5 shadow-ambient">
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-moss/10 text-moss grid place-items-center">{icon}</span>
      <div>
        <p className="text-xs uppercase tracking-wider text-soft">{label}</p>
        <p className="text-2xl font-heading font-bold text-ink">{value}</p>
      </div>
    </div>
  </div>
);

// ---- Users tab ----
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [openProfile, setOpenProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [adminForm, setAdminForm] = useState({ full_name: "", email: "", password: "", phone: "" });
  const [adminBusy, setAdminBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try { await api.put(`/admin/users/${id}`, { subscription_status: status }); toast.success("User updated"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const setActive = async (id, active) => {
    setBusyId(id);
    try { await api.put(`/admin/users/${id}`, { is_active: active }); toast.success(`User ${active ? "activated" : "deactivated"}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const setRole = async (id, role) => {
    if (role === "admin" && !window.confirm("Make this user an admin? They will get full admin access.")) return;
    if (role === "user" && !window.confirm("Revoke admin rights from this user?")) return;
    setBusyId(id);
    try { await api.put(`/admin/users/${id}`, { role }); toast.success(role === "admin" ? "Promoted to admin" : "Demoted to user"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const createAdmin = async (e) => {
    e.preventDefault();
    setAdminBusy(true);
    try {
      await api.post("/admin/users", adminForm);
      toast.success(`Admin "${adminForm.email}" created`);
      setAdminForm({ full_name: "", email: "", password: "", phone: "" });
      setShowAdminForm(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdminBusy(false); }
  };

  return (
    <div data-testid="admin-users-tab">
      {/* Create-admin panel */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted2">{loading ? "Loading…" : `${users.length} user${users.length === 1 ? "" : "s"}`}</p>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="refresh-users"
            className="text-sm text-moss font-medium hover:underline disabled:opacity-50">Refresh</button>
          <button onClick={() => setShowAdminForm((v) => !v)} data-testid="toggle-create-admin"
            className="inline-flex items-center gap-1 bg-moss text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-moss-dark">
            <Plus size={12} /> {showAdminForm ? "Close" : "New admin"}
          </button>
        </div>
      </div>
      {showAdminForm && (
        <form onSubmit={createAdmin} data-testid="create-admin-form"
          className="mb-4 bg-cardm border border-subtle rounded-2xl p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <input required placeholder="Full name" value={adminForm.full_name} onChange={(e) => setAdminForm({ ...adminForm, full_name: e.target.value })}
            data-testid="new-admin-name" className="rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
          <input required type="email" placeholder="Email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
            data-testid="new-admin-email" className="rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
          <input required type="password" placeholder="Password (min 6 chars)" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
            data-testid="new-admin-password" className="rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
          <input placeholder="Phone (optional)" value={adminForm.phone} onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
            data-testid="new-admin-phone" className="rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
          <button type="submit" disabled={adminBusy} data-testid="create-admin-submit"
            className="md:col-span-4 justify-self-start bg-terra hover:bg-terra-dark text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60">
            {adminBusy ? "Creating…" : "Create admin"}
          </button>
        </form>
      )}
      {users.length === 0 ? <Empty>{loading ? "Loading users…" : "No users yet."}</Empty> : (
        <div className="bg-white border border-subtle rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cardm">
              <tr className="text-left text-soft text-xs uppercase tracking-wider">
                <th className="p-4">User</th><th className="p-4">Phone</th><th className="p-4">Status</th><th className="p-4">Active</th><th className="p-4">Joined</th><th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-subtle">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="p-4">
                    <div className="font-medium text-ink">{u.full_name}</div>
                    <div className="text-xs text-muted2">{u.email} {u.role === "admin" && <span className="ml-1 text-moss">(admin)</span>}</div>
                  </td>
                  <td className="p-4 text-muted2">{u.phone || "—"}</td>
                  <td className="p-4">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${u.subscription_status === "paid" ? "bg-leaf/10 text-leaf" : u.subscription_status === "pending" ? "bg-warm/10 text-warm" : "bg-cardm text-muted2"}`}>{u.subscription_status}</span>
                  </td>
                  <td className="p-4">{u.is_active ? <Check size={16} className="text-leaf" /> : <X size={16} className="text-danger" />}</td>
                  <td className="p-4 text-muted2 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="p-4 flex flex-wrap gap-2">
                    {u.subscription_status !== "paid" && (
                      <button onClick={() => setStatus(u.id, "paid")} disabled={busyId === u.id} data-testid={`activate-${u.id}`}
                        className="text-xs bg-leaf text-white rounded-full px-3 py-1.5 font-semibold hover:opacity-90">Grant access</button>
                    )}
                    {u.subscription_status === "paid" && u.role !== "admin" && (
                      <button onClick={() => setStatus(u.id, "free")} disabled={busyId === u.id} data-testid={`revoke-${u.id}`}
                        className="text-xs bg-cardm text-ink rounded-full px-3 py-1.5 font-semibold hover:bg-subtle">Revoke</button>
                    )}
                    {u.role === "admin" ? (
                      <button onClick={() => setRole(u.id, "user")} disabled={busyId === u.id} data-testid={`demote-${u.id}`}
                        className="text-xs border border-subtle text-ink rounded-full px-3 py-1.5 font-medium hover:border-danger hover:text-danger">
                        Revoke admin
                      </button>
                    ) : (
                      <button onClick={() => setRole(u.id, "admin")} disabled={busyId === u.id} data-testid={`promote-${u.id}`}
                        className="text-xs bg-moss text-white rounded-full px-3 py-1.5 font-semibold hover:bg-moss-dark">
                        Make admin
                      </button>
                    )}
                    <button onClick={() => setActive(u.id, !u.is_active)} disabled={busyId === u.id} data-testid={`toggle-active-${u.id}`}
                      className="text-xs border border-subtle rounded-full px-3 py-1.5 font-medium hover:border-moss disabled:opacity-40">
                      {u.is_active ? "Deactivate" : "Activate"}
                    </button>
                    {u.health_profile && (
                      <button onClick={() => setOpenProfile(u)} data-testid={`view-profile-${u.id}`} className="text-xs text-moss hover:underline">View profile</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openProfile && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => setOpenProfile(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold">Health profile — {openProfile.full_name}</h3>
            <pre className="mt-3 bg-cardm p-3 rounded-xl text-xs overflow-auto max-h-80">{JSON.stringify(openProfile.health_profile, null, 2)}</pre>
            <button onClick={() => setOpenProfile(null)} className="mt-4 text-sm text-moss hover:underline">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Subscriptions tab ----
function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const load = () => api.get("/admin/subscriptions").then(({ data }) => setSubs(data));
  useEffect(() => { load(); }, []);

  const action = async (id, kind) => {
    try {
      await api.post(`/admin/subscriptions/${id}/${kind}`);
      toast.success(`Request ${kind}d`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-subscriptions-tab">
      {subs.length === 0 ? <Empty>No subscription requests yet.</Empty> : (
        <div className="bg-white border border-subtle rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cardm">
              <tr className="text-left text-soft text-xs uppercase tracking-wider">
                <th className="p-4">User</th><th className="p-4">Plan</th><th className="p-4">Price</th><th className="p-4">Status</th><th className="p-4">Date</th><th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-subtle">
              {subs.map((s) => (
                <tr key={s.id} data-testid={`sub-row-${s.id}`}>
                  <td className="p-4"><div className="font-medium text-ink">{s.user_name}</div><div className="text-xs text-muted2">{s.user_email}</div></td>
                  <td className="p-4">{s.plan_name}</td>
                  <td className="p-4">{s.plan_price} {s.plan_currency}</td>
                  <td className="p-4">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${s.status === "approved" ? "bg-leaf/10 text-leaf" : s.status === "rejected" ? "bg-danger/10 text-danger" : "bg-warm/10 text-warm"}`}>{s.status}</span>
                  </td>
                  <td className="p-4 text-muted2 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="p-4 flex gap-2">
                    {s.status === "pending" && (
                      <>
                        <button onClick={() => action(s.id, "approve")} data-testid={`approve-${s.id}`} className="text-xs bg-leaf text-white rounded-full px-3 py-1.5 font-semibold hover:opacity-90">Approve</button>
                        <button onClick={() => action(s.id, "reject")} data-testid={`reject-${s.id}`} className="text-xs bg-danger text-white rounded-full px-3 py-1.5 font-semibold hover:opacity-90">Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Videos tab ----
const emptyVideo = { title: "", description: "", youtube_id: "", thumbnail_url: "", is_free_preview: false, order: 0 };
function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVideo);

  const load = () => api.get("/videos").then(({ data }) => setVideos(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: parseInt(form.order || 0) };
      if (editing) await api.put(`/videos/${editing}`, payload);
      else await api.post("/videos", payload);
      toast.success(editing ? "Video updated" : "Video added");
      setForm(emptyVideo); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`); toast.success("Deleted"); await load();
  };
  const edit = (v) => { setEditing(v.id); setForm({ ...v }); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-videos-tab">
      <form onSubmit={save} className="bg-white border border-subtle rounded-2xl p-6 lg:col-span-1 space-y-4">
        <h3 className="font-heading font-semibold text-ink">{editing ? "Edit video" : "Add video"}</h3>
        {[
          ["title", "Title", "text", true],
          ["youtube_id", "YouTube video ID (e.g. dQw4w9WgXcQ)", "text", true],
          ["thumbnail_url", "Thumbnail URL (optional)", "url", false],
          ["order", "Order", "number", false],
        ].map(([k, label, type, req]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-ink mb-1">{label}</label>
            <input type={type} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`video-form-${k}`}
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
        ))}
        <div>
          <label className="block text-xs font-medium text-ink mb-1">Description</label>
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_free_preview} onChange={(e) => setForm({ ...form, is_free_preview: e.target.checked })}
            data-testid="video-form-free-preview" />
          Mark as free preview
        </label>
        <div className="flex gap-2">
          <button type="submit" data-testid="video-save-button" className="bg-moss text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-moss-dark">
            {editing ? "Save" : <span className="inline-flex items-center gap-1"><Plus size={14} /> Add</span>}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(emptyVideo); }} className="text-sm text-muted2 hover:text-ink">Cancel</button>}
        </div>
      </form>

      <div className="lg:col-span-2">
        {videos.length === 0 ? <Empty>No videos yet.</Empty> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {videos.map((v) => (
              <div key={v.id} className="bg-white border border-subtle rounded-2xl p-4 flex gap-3">
                <img src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`} alt="" className="w-28 h-16 object-cover rounded-lg" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-ink truncate">{v.title}</h4>
                  <p className="text-xs text-muted2 truncate">{v.is_free_preview ? "Free preview" : "Paid"} • #{v.order}</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => edit(v)} data-testid={`edit-video-${v.id}`} className="text-xs text-moss hover:underline inline-flex items-center gap-1"><Edit size={12} /> Edit</button>
                    <button onClick={() => del(v.id)} data-testid={`delete-video-${v.id}`} className="text-xs text-danger hover:underline inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- PDFs tab ----
function PdfsTab() {
  const [pdfs, setPdfs] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", is_free_preview: false, order: 0, file: null });

  const load = () => api.get("/pdfs").then(({ data }) => setPdfs(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Please select a PDF file.");
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("is_free_preview", form.is_free_preview);
    fd.append("order", form.order);
    fd.append("file", form.file);
    try {
      await fetch(`${BACKEND_URL}/api/pdfs`, { method: "POST", body: fd, credentials: "include" })
        .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "Upload failed"); });
      toast.success("PDF uploaded");
      setForm({ title: "", description: "", is_free_preview: false, order: 0, file: null });
      document.getElementById("pdf-file-input").value = "";
      await load();
    } catch (err) { toast.error(err.message); }
  };
  const togglePreview = async (p) => {
    await api.put(`/pdfs/${p.id}`, { ...p, is_free_preview: !p.is_free_preview }); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this PDF?")) return;
    await api.delete(`/pdfs/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-pdfs-tab">
      <form onSubmit={upload} className="bg-white border border-subtle rounded-2xl p-6 lg:col-span-1 space-y-4">
        <h3 className="font-heading font-semibold text-ink">Upload PDF</h3>
        <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="pdf-form-title"
          className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
        <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
        <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
          className="w-full rounded-lg border border-subtle px-3 py-2 text-sm" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_free_preview} onChange={(e) => setForm({ ...form, is_free_preview: e.target.checked })} data-testid="pdf-form-free-preview" />
          Mark as free preview
        </label>
        <input id="pdf-file-input" type="file" accept="application/pdf" required
          onChange={(e) => setForm({ ...form, file: e.target.files[0] })} data-testid="pdf-form-file"
          className="block w-full text-sm" />
        <button type="submit" data-testid="pdf-upload-button" className="bg-moss text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-moss-dark inline-flex items-center gap-1">
          <Plus size={14} /> Upload
        </button>
      </form>

      <div className="lg:col-span-2">
        {pdfs.length === 0 ? <Empty>No PDFs uploaded yet.</Empty> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pdfs.map((p) => {
              const fullPublic = `${BACKEND_URL}${p.public_url}`;
              const fullInternal = `${BACKEND_URL}${p.download_url}`;
              const copy = (url, label) => {
                navigator.clipboard.writeText(url);
                toast.success(`${label} link copied`);
              };
              return (
                <div key={p.id} className="bg-white border border-subtle rounded-2xl p-4">
                  <h4 className="font-medium text-ink">{p.title}</h4>
                  <p className="text-xs text-muted2 line-clamp-2">{p.description}</p>
                  <p className="mt-1 text-xs text-soft">{p.is_free_preview ? "Free preview" : "Paid"} • {(p.size_bytes / 1024).toFixed(0)} KB</p>

                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <code className="flex-1 truncate text-[11px] bg-cardm border border-subtle rounded-md px-2 py-1.5">{fullPublic}</code>
                      <button onClick={() => copy(fullPublic, "Public")} data-testid={`copy-public-${p.id}`}
                        className="shrink-0 inline-flex items-center gap-1 bg-moss text-white rounded-md px-2 py-1.5 text-[11px] font-semibold hover:bg-moss-dark">
                        <Copy size={11} /> Public
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 truncate text-[11px] bg-cardm border border-subtle rounded-md px-2 py-1.5">{fullInternal}</code>
                      <button onClick={() => copy(fullInternal, "Paywall")} data-testid={`copy-internal-${p.id}`}
                        className="shrink-0 inline-flex items-center gap-1 bg-ink text-white rounded-md px-2 py-1.5 text-[11px] font-semibold hover:bg-black">
                        <Copy size={11} /> Paywall
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button onClick={() => togglePreview(p)} data-testid={`toggle-preview-${p.id}`} className="text-xs text-moss hover:underline">Toggle preview</button>
                    <button onClick={() => del(p.id)} data-testid={`delete-pdf-${p.id}`} className="text-xs text-danger hover:underline inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Links tab (Linktree-style landing) ----
const emptyButton = () => ({
  id: "",
  label: "",
  type: "url",
  value: "",
  icon: "",
  require_email: false,
  is_active: true,
  order: 0,
});

function LinksTab() {
  const linksUrl = `${window.location.origin}/links`;
  const [cfg, setCfg] = useState({
    profile_image_url: "",
    display_name: "",
    tagline: "",
    bio: "",
    social_instagram: "",
    social_tiktok: "",
    social_youtube: "",
    buttons: [],
  });
  const [busy, setBusy] = useState(false);
  const [leads, setLeads] = useState([]);
  const [libraryPdfs, setLibraryPdfs] = useState([]);

  const loadCfg = () =>
    api.get("/links").then(({ data }) => {
      // /links returns only active buttons; fetch internal admin view from db config too
      setCfg((prev) => ({ ...prev, ...data, buttons: (data.buttons || []).map((b) => ({ ...emptyButton(), ...b })) }));
    }).catch(() => {});
  const loadFull = async () => {
    // get raw config from PUT result? Use a small fetch via admin link endpoint:
    // simpler: keep public /links for initial load; admin can re-add inactive via UI
    await loadCfg();
  };
  const loadLeads = () => api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
  const loadLibraryPdfs = () => api.get("/pdfs").then(({ data }) => setLibraryPdfs(data)).catch(() => {});
  useEffect(() => { loadFull(); loadLeads(); loadLibraryPdfs(); }, []);

  const upd = (k, v) => setCfg({ ...cfg, [k]: v });
  const updBtn = (idx, patch) => {
    const next = [...cfg.buttons];
    next[idx] = { ...next[idx], ...patch };
    setCfg({ ...cfg, buttons: next });
  };
  const addBtn = () => {
    setCfg({
      ...cfg,
      buttons: [...cfg.buttons, { ...emptyButton(), id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, order: cfg.buttons.length }],
    });
  };
  const delBtn = (idx) => {
    const next = cfg.buttons.filter((_, i) => i !== idx).map((b, i) => ({ ...b, order: i }));
    setCfg({ ...cfg, buttons: next });
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cfg.buttons.length) return;
    const next = [...cfg.buttons];
    [next[idx], next[j]] = [next[j], next[idx]];
    setCfg({ ...cfg, buttons: next.map((b, i) => ({ ...b, order: i })) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...cfg,
        buttons: cfg.buttons.map((b, i) => ({
          ...b,
          order: i,
          id: b.id && !b.id.startsWith("tmp_") ? b.id : null,
        })),
      };
      const { data } = await api.put("/admin/links", payload);
      setCfg({ ...cfg, ...data, buttons: data.buttons.map((b) => ({ ...emptyButton(), ...b })) });
      toast.success("Links page saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadAsset = async (idx, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/links/upload-asset`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      const data = await res.json();
      updBtn(idx, { value: data.url });
      toast.success(`Uploaded ${data.filename}`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(linksUrl); toast.success("Links page URL copied"); };

  const downloadCsv = () => {
    window.open(`${BACKEND_URL}/api/admin/leads.csv`, "_blank");
  };

  return (
    <div className="space-y-8" data-testid="admin-links-tab">
      {/* Header / shareable URL */}
      <div className="bg-white border border-subtle rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="font-heading font-semibold text-ink flex items-center gap-2"><Link2 size={18} className="text-moss" /> Public links page</h3>
          <p className="text-sm text-muted2 mt-1">Shareable mobile-first page (not linked from the public menu).</p>
        </div>
        <div className="flex items-center gap-2">
          <code className="text-xs bg-cardm border border-subtle rounded-full px-3 py-1.5 truncate max-w-[14rem]" data-testid="links-url">{linksUrl}</code>
          <button onClick={copyLink} data-testid="copy-links-url" className="inline-flex items-center gap-1 bg-moss text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-moss-dark">
            <Copy size={12} /> Copy
          </button>
          <a href="/links" target="_blank" rel="noopener noreferrer" className="text-xs text-moss hover:underline">Preview</a>
        </div>
      </div>

      {/* Header config */}
      <div className="bg-white border border-subtle rounded-2xl p-6 space-y-4">
        <h3 className="font-heading font-semibold text-ink">Page header</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-ink mb-1">Profile image URL</label>
            <input value={cfg.profile_image_url} onChange={(e) => upd("profile_image_url", e.target.value)}
              placeholder="https://… or /uploads/links/…" data-testid="links-profile-image-url"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
            <label className="text-xs text-moss font-medium mt-2 inline-flex items-center gap-1 cursor-pointer hover:underline">
              <input type="file" accept="image/*" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    const r = await fetch(`${BACKEND_URL}/api/admin/links/upload-asset`, { method: "POST", body: fd, credentials: "include" });
                    if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
                    const data = await r.json();
                    upd("profile_image_url", data.url);
                    toast.success("Image uploaded");
                  } catch (err) { toast.error(err.message); }
                }}
              />
              Upload image
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink mb-1">Display name</label>
            <input value={cfg.display_name} onChange={(e) => upd("display_name", e.target.value)}
              data-testid="links-display-name"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink mb-1">Tagline</label>
            <input value={cfg.tagline} onChange={(e) => upd("tagline", e.target.value)}
              data-testid="links-tagline"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink mb-1">Short bio (optional)</label>
            <input value={cfg.bio} onChange={(e) => upd("bio", e.target.value)}
              data-testid="links-bio"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink mb-1">Instagram URL</label>
            <input value={cfg.social_instagram} onChange={(e) => upd("social_instagram", e.target.value)} placeholder="https://instagram.com/…"
              data-testid="links-social-ig"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink mb-1">TikTok URL</label>
            <input value={cfg.social_tiktok} onChange={(e) => upd("social_tiktok", e.target.value)} placeholder="https://tiktok.com/@…"
              data-testid="links-social-tt"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink mb-1">YouTube URL</label>
            <input value={cfg.social_youtube} onChange={(e) => upd("social_youtube", e.target.value)} placeholder="https://youtube.com/@…"
              data-testid="links-social-yt"
              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          </div>
        </div>
      </div>

      {/* Buttons list */}
      <div className="bg-white border border-subtle rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-ink">Buttons ({cfg.buttons.length})</h3>
          <button onClick={addBtn} data-testid="add-link-button"
            className="inline-flex items-center gap-1 bg-moss text-white rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-moss-dark">
            <Plus size={14} /> Add button
          </button>
        </div>
        {cfg.buttons.length === 0 && <Empty>No buttons yet. Click "Add button" to create one.</Empty>}
        <div className="space-y-3">
          {cfg.buttons.map((b, i) => (
            <div key={b.id || i} className="border border-subtle rounded-2xl p-4 bg-cardm/40" data-testid={`link-button-row-${i}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center pt-2 text-soft">
                  <GripVertical size={14} />
                  <button type="button" onClick={() => move(i, -1)} aria-label="Move up" className="text-xs hover:text-moss px-1">▲</button>
                  <button type="button" onClick={() => move(i, +1)} aria-label="Move down" className="text-xs hover:text-moss px-1">▼</button>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-3">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Type</label>
                    <select value={b.type} onChange={(e) => updBtn(i, { type: e.target.value })} data-testid={`btn-type-${i}`}
                      className="w-full rounded-lg border border-subtle px-2.5 py-2 text-sm">
                      <option value="url">External URL</option>
                      <option value="pdf">PDF download</option>
                      <option value="video">Video / YouTube</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="email">Email (mailto)</option>
                    </select>
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Label</label>
                    <input value={b.label} onChange={(e) => updBtn(i, { label: e.target.value })} data-testid={`btn-label-${i}`}
                      placeholder="Free meal plan PDF"
                      className="w-full rounded-lg border border-subtle px-3 py-2 text-sm" />
                  </div>
                  <div className="md:col-span-5">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">
                      {b.type === "pdf" ? "Choose PDF from library" :
                       b.type === "whatsapp" ? "Phone number (with country code, no +)" :
                       b.type === "email" ? "Email address" : "URL"}
                    </label>
                    {b.type === "pdf" ? (
                      <div className="space-y-2">
                        <select
                          value={b.value && (b.value.startsWith("/api/uploads/pdfs/") || b.value.startsWith("/uploads/pdfs/")) ? b.value : ""}
                          onChange={(e) => updBtn(i, { value: e.target.value })}
                          data-testid={`btn-pdf-select-${i}`}
                          className="w-full rounded-lg border border-subtle px-3 py-2 text-sm bg-white"
                        >
                          <option value="">— Select a PDF —</option>
                          {libraryPdfs.map((p) => (
                            <option key={p.id} value={p.public_url}>
                              {p.title}{p.is_free_preview ? "" : " (paid — will be public when shared here)"}
                            </option>
                          ))}
                        </select>
                        <details className="text-xs text-muted2">
                          <summary className="cursor-pointer hover:text-moss">Or paste an external URL / upload new PDF</summary>
                          <div className="mt-2 space-y-2">
                            <input value={b.value} onChange={(e) => updBtn(i, { value: e.target.value })} data-testid={`btn-value-${i}`}
                              placeholder="https://… or /uploads/links/file.pdf"
                              className="w-full rounded-lg border border-subtle px-3 py-2 text-sm" />
                            <label className="text-xs text-moss font-medium inline-flex items-center gap-1 cursor-pointer hover:underline">
                              <input type="file" accept="application/pdf" className="hidden"
                                onChange={(e) => uploadAsset(i, e.target.files?.[0])}
                                data-testid={`btn-pdf-upload-${i}`} />
                              Upload new PDF
                            </label>
                          </div>
                        </details>
                        {b.value && (b.value.startsWith("/api/uploads/") || b.value.startsWith("/uploads/")) && (
                          <p className="text-[11px] text-soft">Direct download URL — no login required.</p>
                        )}
                      </div>
                    ) : (
                      <input value={b.value} onChange={(e) => updBtn(i, { value: e.target.value })} data-testid={`btn-value-${i}`}
                        placeholder={
                          b.type === "whatsapp" ? "971500000000" :
                          b.type === "email" ? "you@example.com" :
                          "https://…"
                        }
                        className="w-full rounded-lg border border-subtle px-3 py-2 text-sm" />
                    )}
                  </div>
                  <div className="md:col-span-12 flex flex-wrap items-center gap-4 pt-1">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!b.require_email}
                        onChange={(e) => updBtn(i, { require_email: e.target.checked })}
                        data-testid={`btn-require-email-${i}`} />
                      Ask for email before action
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={b.is_active !== false}
                        onChange={(e) => updBtn(i, { is_active: e.target.checked })}
                        data-testid={`btn-active-${i}`} />
                      Active
                    </label>
                    <button type="button" onClick={() => delBtn(i)} data-testid={`btn-delete-${i}`}
                      className="ml-auto inline-flex items-center gap-1 text-danger text-sm hover:underline">
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="pt-2">
          <button onClick={save} disabled={busy} data-testid="links-save-button"
            className="bg-terra hover:bg-terra-dark text-white rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60">
            {busy ? "Saving…" : "Save links page"}
          </button>
        </div>
      </div>

      {/* Leads */}
      <div className="bg-white border border-subtle rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-ink">Captured leads ({leads.length})</h3>
          <div className="flex items-center gap-2">
            <button onClick={loadLeads} className="text-xs text-moss hover:underline" data-testid="refresh-leads">Refresh</button>
            <button onClick={downloadCsv} data-testid="export-leads-csv"
              className="inline-flex items-center gap-1 bg-moss text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-moss-dark">
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>
        {leads.length === 0 ? <Empty>No leads captured yet.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cardm">
                <tr className="text-left text-soft text-xs uppercase tracking-wider">
                  <th className="p-3">Captured</th><th className="p-3">Email</th><th className="p-3">Button</th><th className="p-3">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-subtle">
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                    <td className="p-3 text-muted2 text-xs whitespace-nowrap">{new Date(l.captured_at).toLocaleString()}</td>
                    <td className="p-3 font-medium text-ink">{l.email}</td>
                    <td className="p-3">{l.button_label}</td>
                    <td className="p-3 text-xs uppercase tracking-wider text-soft">{l.button_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Pages tab (CMS for the public landing page) ----
const Section = ({ title, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-subtle rounded-2xl" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left">
        <span className="font-heading font-semibold text-ink">{title}</span>
        {open ? <ChevronDown size={18} className="text-soft" /> : <ChevronRight size={18} className="text-soft" />}
      </button>
      {open && <div className="px-6 pb-6 pt-2 space-y-4 border-t border-subtle">{children}</div>}
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-ink mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-soft mt-1">{hint}</p>}
  </div>
);

const Input = (props) => (
  <input {...props}
    className={`w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 ${props.className || ""}`} />
);
const Textarea = (props) => (
  <textarea {...props}
    className={`w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 ${props.className || ""}`} />
);

function PagesTab() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/page-content").then(({ data }) => setC(data));
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setC({ ...c, [k]: v });
  const updArr = (k, idx, patch) => {
    const next = [...c[k]];
    next[idx] = typeof patch === "function" ? patch(next[idx]) : { ...next[idx], ...patch };
    setC({ ...c, [k]: next });
  };
  const updStrArr = (k, idx, value) => {
    const next = [...c[k]]; next[idx] = value;
    setC({ ...c, [k]: next });
  };
  const addItem = (k, item) => setC({ ...c, [k]: [...(c[k] || []), item] });
  const delItem = (k, idx) => setC({ ...c, [k]: c[k].filter((_, i) => i !== idx) });

  const uploadHeroImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/links/upload-asset`, { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      const data = await r.json();
      upd("hero_image_url", data.url);
      toast.success("Hero image uploaded");
    } catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/page-content", c);
      invalidatePageContent();
      toast.success("Page content saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!c) return <Empty>Loading…</Empty>;

  return (
    <div className="space-y-4" data-testid="admin-pages-tab">
      <div className="bg-cardm border border-subtle rounded-2xl p-4 flex items-center justify-between">
        <p className="text-sm text-muted2">Edit every section of the public landing page. Changes go live immediately after save.</p>
        <button onClick={save} disabled={busy} data-testid="pages-save-button"
          className="bg-terra hover:bg-terra-dark text-white rounded-full px-6 py-2 text-sm font-semibold disabled:opacity-60">
          {busy ? "Saving…" : "Save all changes"}
        </button>
      </div>

      <Section title="Hero section" defaultOpen testId="section-hero">
        <Field label="Small badge text"><Input value={c.hero_badge} onChange={(e) => upd("hero_badge", e.target.value)} data-testid="hero-badge" /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Title (left side, dark)"><Input value={c.hero_title} onChange={(e) => upd("hero_title", e.target.value)} data-testid="hero-title" /></Field>
          <Field label="Highlight (right side, green)"><Input value={c.hero_highlight} onChange={(e) => upd("hero_highlight", e.target.value)} data-testid="hero-highlight" /></Field>
        </div>
        <Field label="Subtitle paragraph"><Textarea rows={3} value={c.hero_subtitle} onChange={(e) => upd("hero_subtitle", e.target.value)} data-testid="hero-subtitle" /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary CTA label"><Input value={c.hero_cta_primary} onChange={(e) => upd("hero_cta_primary", e.target.value)} /></Field>
          <Field label="Secondary CTA label"><Input value={c.hero_cta_secondary} onChange={(e) => upd("hero_cta_secondary", e.target.value)} /></Field>
        </div>
        <Field label="Hero image URL" hint="Or use the upload button below. Defaults to a healthy-ingredients photo.">
          <Input value={c.hero_image_url} onChange={(e) => upd("hero_image_url", e.target.value)} placeholder="https://… or /api/uploads/links/…" />
          <label className="text-xs text-moss font-medium mt-2 inline-flex items-center gap-1 cursor-pointer hover:underline">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHeroImage(e.target.files?.[0])} />
            Upload image
          </label>
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Image caption — title"><Input value={c.hero_image_caption_title} onChange={(e) => upd("hero_image_caption_title", e.target.value)} /></Field>
          <Field label="Image caption — subtitle"><Input value={c.hero_image_caption_subtitle} onChange={(e) => upd("hero_image_caption_subtitle", e.target.value)} /></Field>
        </div>
      </Section>

      <Section title="Benefits section" testId="section-benefits">
        <Field label="Section title"><Input value={c.benefits_title} onChange={(e) => upd("benefits_title", e.target.value)} /></Field>
        <Field label="Section subtitle"><Textarea rows={2} value={c.benefits_subtitle} onChange={(e) => upd("benefits_subtitle", e.target.value)} /></Field>
        <div className="space-y-3">
          {c.benefits.map((b, i) => (
            <div key={i} className="border border-subtle rounded-xl p-3 grid md:grid-cols-12 gap-2 bg-cardm/40" data-testid={`benefit-row-${i}`}>
              <div className="md:col-span-2">
                <select value={b.icon} onChange={(e) => updArr("benefits", i, { icon: e.target.value })} className="w-full rounded-lg border border-subtle px-2 py-2 text-sm">
                  <option value="play">▶ Play</option>
                  <option value="file">📄 File</option>
                  <option value="salad">🥗 Salad</option>
                </select>
              </div>
              <div className="md:col-span-4"><Input value={b.title} onChange={(e) => updArr("benefits", i, { title: e.target.value })} placeholder="Title" /></div>
              <div className="md:col-span-5"><Input value={b.description} onChange={(e) => updArr("benefits", i, { description: e.target.value })} placeholder="Description" /></div>
              <div className="md:col-span-1 grid place-items-center">
                <button onClick={() => delItem("benefits", i)} className="text-danger hover:opacity-70"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <button onClick={() => addItem("benefits", { title: "", description: "", icon: "play" })}
            className="inline-flex items-center gap-1 text-sm text-moss hover:underline"><Plus size={14} /> Add benefit</button>
        </div>
      </Section>

      <Section title="About Dana" testId="section-about">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name"><Input value={c.about_name} onChange={(e) => upd("about_name", e.target.value)} /></Field>
          <Field label="Role / tagline"><Input value={c.about_role} onChange={(e) => upd("about_role", e.target.value)} /></Field>
        </div>
        <Field label="Credentials">
          <div className="space-y-2">
            {c.about_credentials.map((cr, i) => (
              <div key={i} className="grid md:grid-cols-12 gap-2 border border-subtle rounded-xl p-3 bg-cardm/40">
                <div className="md:col-span-2">
                  <select value={cr.icon} onChange={(e) => updArr("about_credentials", i, { icon: e.target.value })} className="w-full rounded-lg border border-subtle px-2 py-2 text-sm">
                    <option value="graduation">🎓 Graduation</option>
                    <option value="award">🏆 Award</option>
                  </select>
                </div>
                <div className="md:col-span-5"><Input value={cr.title} onChange={(e) => updArr("about_credentials", i, { title: e.target.value })} placeholder="Title" /></div>
                <div className="md:col-span-4"><Input value={cr.subtitle} onChange={(e) => updArr("about_credentials", i, { subtitle: e.target.value })} placeholder="Subtitle" /></div>
                <div className="md:col-span-1 grid place-items-center"><button onClick={() => delItem("about_credentials", i)} className="text-danger hover:opacity-70"><Trash2 size={14} /></button></div>
              </div>
            ))}
            <button onClick={() => addItem("about_credentials", { title: "", subtitle: "", icon: "graduation" })} className="text-sm text-moss hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add credential</button>
          </div>
        </Field>
        <Field label="Bio paragraphs">
          <div className="space-y-2">
            {c.about_paragraphs.map((p, i) => (
              <div key={i} className="flex items-start gap-2">
                <Textarea rows={3} value={p} onChange={(e) => updStrArr("about_paragraphs", i, e.target.value)} className="flex-1" />
                <button onClick={() => delItem("about_paragraphs", i)} className="text-danger hover:opacity-70 mt-2"><Trash2 size={14} /></button>
              </div>
            ))}
            <button onClick={() => addItem("about_paragraphs", "")} className="text-sm text-moss hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add paragraph</button>
          </div>
        </Field>
        <Field label="Areas of expertise">
          <div className="space-y-2">
            {c.about_expertise.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={t} onChange={(e) => updStrArr("about_expertise", i, e.target.value)} className="flex-1" />
                <button onClick={() => delItem("about_expertise", i)} className="text-danger hover:opacity-70"><Trash2 size={14} /></button>
              </div>
            ))}
            <button onClick={() => addItem("about_expertise", "")} className="text-sm text-moss hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add expertise</button>
          </div>
        </Field>
        <Field label="Philosophy quote (no surrounding quotes needed)">
          <Textarea rows={3} value={c.about_philosophy_quote} onChange={(e) => upd("about_philosophy_quote", e.target.value)} />
        </Field>
        <Field label="Philosophy supporting paragraph">
          <Textarea rows={3} value={c.about_philosophy_text} onChange={(e) => upd("about_philosophy_text", e.target.value)} />
        </Field>
      </Section>

      <Section title="How it works" testId="section-how">
        <Field label="Section title"><Input value={c.how_title} onChange={(e) => upd("how_title", e.target.value)} /></Field>
        <Field label="Section subtitle"><Textarea rows={2} value={c.how_subtitle} onChange={(e) => upd("how_subtitle", e.target.value)} /></Field>
        <div className="space-y-2">
          {c.how_steps.map((s, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-2 border border-subtle rounded-xl p-3 bg-cardm/40">
              <div className="md:col-span-4"><Input value={s.title} onChange={(e) => updArr("how_steps", i, { title: e.target.value })} placeholder="Step title" /></div>
              <div className="md:col-span-7"><Input value={s.description} onChange={(e) => updArr("how_steps", i, { description: e.target.value })} placeholder="Step description" /></div>
              <div className="md:col-span-1 grid place-items-center"><button onClick={() => delItem("how_steps", i)} className="text-danger hover:opacity-70"><Trash2 size={14} /></button></div>
            </div>
          ))}
          <button onClick={() => addItem("how_steps", { title: "", description: "" })} className="text-sm text-moss hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add step</button>
        </div>
      </Section>

      <Section title="What you get" testId="section-what">
        <Field label="Section title"><Input value={c.what_title} onChange={(e) => upd("what_title", e.target.value)} /></Field>
        <Field label="Bullet items">
          <div className="space-y-2">
            {c.what_items.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={t} onChange={(e) => updStrArr("what_items", i, e.target.value)} className="flex-1" />
                <button onClick={() => delItem("what_items", i)} className="text-danger hover:opacity-70"><Trash2 size={14} /></button>
              </div>
            ))}
            <button onClick={() => addItem("what_items", "")} className="text-sm text-moss hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add bullet</button>
          </div>
        </Field>
      </Section>

      <Section title="FAQ" testId="section-faq">
        <div className="space-y-3">
          {c.faq_items.map((f, i) => (
            <div key={i} className="border border-subtle rounded-xl p-3 bg-cardm/40 space-y-2" data-testid={`faq-row-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={f.q} onChange={(e) => updArr("faq_items", i, { q: e.target.value })} placeholder="Question" className="flex-1" />
                <button onClick={() => delItem("faq_items", i)} className="text-danger hover:opacity-70"><Trash2 size={14} /></button>
              </div>
              <Textarea rows={2} value={f.a} onChange={(e) => updArr("faq_items", i, { a: e.target.value })} placeholder="Answer" />
            </div>
          ))}
          <button onClick={() => addItem("faq_items", { q: "", a: "" })} className="text-sm text-moss hover:underline inline-flex items-center gap-1"><Plus size={14} /> Add question</button>
        </div>
      </Section>

      <Section title="Footer & contact" testId="section-footer">
        <Field label="Footer tagline"><Textarea rows={3} value={c.footer_tagline} onChange={(e) => upd("footer_tagline", e.target.value)} /></Field>
        <Field label="WhatsApp number (digits only, with country code, no +)" hint="Example: 971500000000">
          <Input value={c.whatsapp_number} onChange={(e) => upd("whatsapp_number", e.target.value)} data-testid="whatsapp-number-input" />
        </Field>
      </Section>

      <div className="bg-cardm border border-subtle rounded-2xl p-4 flex items-center justify-end">
        <button onClick={save} disabled={busy}
          className="bg-terra hover:bg-terra-dark text-white rounded-full px-6 py-2 text-sm font-semibold disabled:opacity-60">
          {busy ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </div>
  );
}

// ---- Settings tab (welcome page / campaign) ----
function SettingsTab() {
  const [form, setForm] = useState({
    welcome_video_youtube_id: "",
    welcome_title: "",
    welcome_subtitle: "",
    welcome_cta_label: "",
    public_base_url: "",
  });
  const [busy, setBusy] = useState(false);
  const baseUrl = (form.public_base_url || "").replace(/\/+$/, "");
  const welcomeUrl = `${baseUrl || window.location.origin}/welcome`;

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => setForm({ ...form, ...data })).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cleanedId = extractYouTubeId(form.welcome_video_youtube_id);
      const payload = { ...form, welcome_video_youtube_id: cleanedId };
      await api.put("/admin/site-settings", payload);
      setForm(payload);
      toast.success(cleanedId || !form.welcome_video_youtube_id
        ? "Welcome page updated"
        : "Saved (couldn't detect a YouTube ID — leaving blank)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(welcomeUrl);
    toast.success("Campaign link copied");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-settings-tab">
      <form onSubmit={save} className="bg-white border border-subtle rounded-2xl p-6 lg:col-span-2 space-y-4">
        <h3 className="font-heading font-semibold text-ink">Welcome (campaign) page</h3>
        <p className="text-sm text-muted2">This is the hidden landing page you can share in social media campaigns. It is not linked anywhere on the public site.</p>
        <div>
          <label className="block text-xs font-medium text-ink mb-1">YouTube video ID or URL</label>
          <input value={form.welcome_video_youtube_id}
            onChange={(e) => setForm({ ...form, welcome_video_youtube_id: e.target.value })}
            onBlur={(e) => {
              const cleaned = extractYouTubeId(e.target.value);
              if (cleaned && cleaned !== e.target.value) {
                setForm({ ...form, welcome_video_youtube_id: cleaned });
              }
            }}
            placeholder="dQw4w9WgXcQ  or  https://youtu.be/dQw4w9WgXcQ" data-testid="settings-youtube-id"
            className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          <p className="text-xs text-soft mt-1">
            Paste either the 11-char ID or a full YouTube URL (watch, youtu.be, embed, shorts) — we'll auto-extract the ID. Leave blank to show a placeholder.
          </p>
          {form.welcome_video_youtube_id && !extractYouTubeId(form.welcome_video_youtube_id) && (
            <p className="text-xs text-danger mt-1" data-testid="yt-id-warning">
              Could not detect a valid YouTube ID in that text.
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-ink mb-1">Welcome title</label>
          <input value={form.welcome_title} onChange={(e) => setForm({ ...form, welcome_title: e.target.value })}
            data-testid="settings-title"
            className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink mb-1">Welcome subtitle</label>
          <textarea rows={2} value={form.welcome_subtitle}
            onChange={(e) => setForm({ ...form, welcome_subtitle: e.target.value })}
            data-testid="settings-subtitle"
            className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink mb-1">Public base URL</label>
          <input value={form.public_base_url}
            onChange={(e) => setForm({ ...form, public_base_url: e.target.value })}
            placeholder="https://danashakaa.com" data-testid="settings-public-base-url"
            className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
          <p className="text-xs text-soft mt-1">Used for the campaign link displayed below. No trailing slash.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink mb-1">CTA button label</label>
          <input value={form.welcome_cta_label}
            onChange={(e) => setForm({ ...form, welcome_cta_label: e.target.value })}
            data-testid="settings-cta-label"
            className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
        </div>
        <button type="submit" disabled={busy} data-testid="settings-save-button"
          className="bg-moss hover:bg-moss-dark text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>

      <div className="bg-white border border-subtle rounded-2xl p-6 space-y-4">
        <h3 className="font-heading font-semibold text-ink">Campaign link</h3>
        <p className="text-sm text-muted2">Share this URL in your social media ads, posts, or stories.</p>
        <div className="rounded-xl border border-subtle bg-cardm p-3 flex items-center justify-between gap-2">
          <code className="text-xs text-ink truncate" data-testid="campaign-link">{welcomeUrl}</code>
          <button onClick={copyLink} data-testid="copy-campaign-link"
            className="shrink-0 inline-flex items-center gap-1 bg-moss text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-moss-dark">
            <Copy size={12} /> Copy
          </button>
        </div>
        <a href="/welcome" target="_blank" rel="noopener noreferrer" data-testid="preview-welcome"
          className="inline-flex items-center gap-1 text-sm text-moss font-medium hover:underline">
          Preview welcome page <ArrowRightTiny />
        </a>
      </div>
    </div>
  );
}
const ArrowRightTiny = () => <span aria-hidden>→</span>;

// ---- Plans tab ----
const emptyPlan = { name: "", type: "monthly", price: 0, currency: "AED", benefits: "", is_active: true };
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(emptyPlan);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price), benefits: form.benefits.split("\n").map(s => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/plans/${editing}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(editing ? "Plan updated" : "Plan added");
      setForm(emptyPlan); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (p) => { setEditing(p.id); setForm({ ...p, benefits: (p.benefits || []).join("\n") }); };
  const del = async (id) => { if (!window.confirm("Delete plan?")) return; await api.delete(`/admin/plans/${id}`); load(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-plans-tab">
      <form onSubmit={save} className="bg-white border border-subtle rounded-2xl p-6 lg:col-span-1 space-y-4">
        <h3 className="font-heading font-semibold text-ink">{editing ? "Edit plan" : "New plan"}</h3>
        <input required placeholder="Plan name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="plan-form-name" className="w-full rounded-lg border border-subtle px-3 py-2 text-sm focus:border-moss focus:outline-none" />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
          data-testid="plan-form-type" className="w-full rounded-lg border border-subtle px-3 py-2 text-sm">
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="lifetime">Lifetime</option>
        </select>
        <div className="grid grid-cols-2 gap-2">
          <input required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
            data-testid="plan-form-price" className="rounded-lg border border-subtle px-3 py-2 text-sm" />
          <input placeholder="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
            className="rounded-lg border border-subtle px-3 py-2 text-sm" />
        </div>
        <textarea rows={5} placeholder="Benefits (one per line)" value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })}
          data-testid="plan-form-benefits" className="w-full rounded-lg border border-subtle px-3 py-2 text-sm" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Active
        </label>
        <div className="flex gap-2">
          <button type="submit" data-testid="plan-save-button" className="bg-moss text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-moss-dark">{editing ? "Save" : "Add plan"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(emptyPlan); }} className="text-sm text-muted2">Cancel</button>}
        </div>
      </form>

      <div className="lg:col-span-2 space-y-4">
        {plans.length === 0 ? <Empty>No plans yet.</Empty> : plans.map((p) => (
          <div key={p.id} className="bg-white border border-subtle rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-heading font-semibold text-ink">{p.name} <span className="text-xs text-soft uppercase ml-2">{p.type}</span></h4>
                <p className="text-sm text-muted2">{p.price} {p.currency}{p.is_active ? "" : " • inactive"}</p>
                <ul className="mt-2 text-sm text-muted2 list-disc list-inside space-y-0.5">
                  {(p.benefits || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => edit(p)} data-testid={`edit-plan-${p.id}`} className="text-xs text-moss hover:underline">Edit</button>
                <button onClick={() => del(p.id)} data-testid={`delete-plan-${p.id}`} className="text-xs text-danger hover:underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <div className="container-page py-10" data-testid="admin-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">Admin dashboard</h1>
      <p className="mt-2 text-muted2">Manage users, content, and subscriptions.</p>

      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Users" value={stats.total_users} icon={<Users size={18} />} />
          <Stat label="Paid" value={stats.paid_users} icon={<Crown size={18} />} />
          <Stat label="Pending" value={stats.pending_users} icon={<ListPlus size={18} />} />
          <Stat label="Videos" value={stats.total_videos} icon={<Video size={18} />} />
          <Stat label="PDFs" value={stats.total_pdfs} icon={<FileText size={18} />} />
        </div>
      )}

      <Tabs defaultValue="users" className="mt-8">
        <TabsList className="bg-white border border-subtle rounded-full p-1 inline-flex flex-wrap gap-1" data-testid="admin-tabs">
          <TabsTrigger value="users" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="subs" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="videos" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-videos">Videos</TabsTrigger>
          <TabsTrigger value="pdfs" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-pdfs">PDFs</TabsTrigger>
          <TabsTrigger value="plans" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="links" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-links">Links</TabsTrigger>
          <TabsTrigger value="pages" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-pages">Pages</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full px-4 data-[state=active]:bg-moss data-[state=active]:text-white" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="subs" className="mt-6"><SubscriptionsTab /></TabsContent>
        <TabsContent value="videos" className="mt-6"><VideosTab /></TabsContent>
        <TabsContent value="pdfs" className="mt-6"><PdfsTab /></TabsContent>
        <TabsContent value="plans" className="mt-6"><PlansTab /></TabsContent>
        <TabsContent value="links" className="mt-6"><LinksTab /></TabsContent>
        <TabsContent value="pages" className="mt-6"><PagesTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
